import { useCallback, useEffect, useRef, useState } from 'react';
import {
  CloseOutlined as CloseOutlinedIcon,
  DownloadOutlined as DownloadOutlinedIcon,
  OpenInNewOutlined as OpenInNewOutlinedIcon,
} from '@mui/icons-material';
import {
  Alert,
  AppBar,
  Box,
  CircularProgress,
  Dialog,
  IconButton,
  Stack,
  Toolbar,
  Tooltip,
  Typography,
} from '@mui/material';

import { useGetClinicDocumentFileQuery } from '../store';

const isPdfContentType = (contentType: string): boolean =>
  contentType === 'application/pdf' || contentType.endsWith('.pdf');

const isImageContentType = (contentType: string): boolean =>
  contentType.startsWith('image/');

interface ClinicDocumentFileViewerProps {
  open: boolean;
  onClose: () => void;
  clinicDocumentId: string | null;
  contentType: string;
  filename: string;
  title?: string | null;
  /** Optional page number to navigate to when viewing a PDF */
  initialPage?: number | null;
}

export function ClinicDocumentFileViewer({
  open,
  onClose,
  clinicDocumentId,
  contentType,
  filename,
  title,
  initialPage,
}: ClinicDocumentFileViewerProps) {
  const { data: blob, isLoading, error } = useGetClinicDocumentFileQuery(
    { clinicDocumentId: clinicDocumentId ?? '' },
    { skip: !clinicDocumentId || !open },
  );

  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const blobUrlRef = useRef<string | null>(null);

  useEffect(() => {
    if (blob) {
      const url = URL.createObjectURL(blob);
      blobUrlRef.current = url;
      setBlobUrl(url);
    }

    return () => {
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = null;
        setBlobUrl(null);
      }
    };
  }, [blob]);

  const isPdf = isPdfContentType(contentType);
  const isImage = isImageContentType(contentType);

  const handleOpenInNewTab = useCallback(() => {
    if (blobUrl) {
      window.open(blobUrl, '_blank');
    }
  }, [blobUrl]);

  const handleDownload = useCallback(() => {
    if (!blobUrl) return;
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = filename;
    link.click();
  }, [blobUrl, filename]);

  return (
    <Dialog fullScreen open={open} onClose={onClose}>
      <AppBar sx={{ position: 'relative' }} color="default" elevation={1}>
        <Toolbar>
          <IconButton edge="start" onClick={onClose} aria-label="Close file viewer">
            <CloseOutlinedIcon />
          </IconButton>
          <Typography variant="h6" sx={{ fontWeight: 600, ml: 1, flex: 1 }} noWrap>
            {title || filename}
          </Typography>
          <Stack direction="row" spacing={0.5}>
            <Tooltip title="Open in new tab">
              <span>
                <IconButton
                  onClick={handleOpenInNewTab}
                  disabled={!blobUrl}
                  aria-label="Open in new tab"
                >
                  <OpenInNewOutlinedIcon />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Download">
              <span>
                <IconButton
                  onClick={handleDownload}
                  disabled={!blobUrl}
                  aria-label="Download file"
                >
                  <DownloadOutlinedIcon />
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
        </Toolbar>
      </AppBar>

      <Box
        sx={{
          flex: 1,
          overflow: 'auto',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'grey.100',
        }}
      >
        {isLoading && (
          <Stack spacing={2} sx={{ alignItems: "center" }}>
            <CircularProgress size={32} />
            <Typography variant="body2" color="text.secondary">
              Loading file...
            </Typography>
          </Stack>
        )}

        {error && (
          <Alert severity="error" sx={{ maxWidth: 500 }}>
            Failed to load the original file. The streaming endpoint may not be available yet.
          </Alert>
        )}

        {!isLoading && !error && blobUrl && isPdf && (
          <Box
            component="iframe"
            src={initialPage ? `${blobUrl}#page=${initialPage}` : blobUrl}
            sx={{ width: '100%', height: '100%', border: 'none' }}
            title={title || filename}
          />
        )}

        {!isLoading && !error && blobUrl && isImage && (
          <Box
            component="img"
            src={blobUrl}
            alt={title || filename}
            sx={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
          />
        )}

        {!isLoading && !error && blobUrl && !isPdf && !isImage && (
          <Alert severity="info" sx={{ maxWidth: 500 }}>
            This file type ({contentType}) cannot be previewed. Use the download button to save it
            locally.
          </Alert>
        )}
      </Box>
    </Dialog>
  );
}
